import { useEffect, useRef } from 'react'
import './AnimatedGlobe.css'

export default function AnimatedGlobe() {
    const canvasRef = useRef(null)

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        const ctx = canvas.getContext('2d')
        const size = 40
        const dpr = window.devicePixelRatio || 1

        canvas.width = size * dpr
        canvas.height = size * dpr
        canvas.style.width = size + 'px'
        canvas.style.height = size + 'px'
        ctx.scale(dpr, dpr)

        let rotation = 0
        const tiltAngle = Math.PI / 8 // 23.5 degrees tilt (like Earth)

        function drawGlobe() {
            ctx.clearRect(0, 0, size, size)

            const centerX = size / 2
            const centerY = size / 2
            const radius = size / 2 - 3

            // Outer space glow
            const outerGlow = ctx.createRadialGradient(
                centerX, centerY, radius * 0.7,
                centerX, centerY, radius * 1.3
            )
            outerGlow.addColorStop(0, 'rgba(59, 130, 246, 0.3)')
            outerGlow.addColorStop(0.5, 'rgba(37, 99, 235, 0.15)')
            outerGlow.addColorStop(1, 'rgba(37, 99, 235, 0)')

            ctx.fillStyle = outerGlow
            ctx.beginPath()
            ctx.arc(centerX, centerY, radius * 1.3, 0, Math.PI * 2)
            ctx.fill()

            // Globe sphere base
            const baseGradient = ctx.createRadialGradient(
                centerX - radius * 0.35,
                centerY - radius * 0.35,
                radius * 0.1,
                centerX,
                centerY,
                radius
            )
            baseGradient.addColorStop(0, '#3b82f6')
            baseGradient.addColorStop(0.4, '#2563eb')
            baseGradient.addColorStop(0.8, '#1e40af')
            baseGradient.addColorStop(1, '#1e3a8a')

            ctx.fillStyle = baseGradient
            ctx.beginPath()
            ctx.arc(centerX, centerY, radius, 0, Math.PI * 2)
            ctx.fill()

            // Apply rotation transformation
            ctx.save()
            ctx.translate(centerX, centerY)

            // Latitude lines (horizontal)
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)'
            ctx.lineWidth = 0.8

            const numLatLines = 8
            for (let i = 1; i < numLatLines; i++) {
                const lat = (i / numLatLines - 0.5) * Math.PI
                const y = Math.sin(lat) * radius * 0.95
                const ellipseWidth = Math.cos(lat) * radius * 0.95

                if (ellipseWidth > 0) {
                    ctx.beginPath()
                    ctx.ellipse(0, y, ellipseWidth, ellipseWidth * 0.15, 0, 0, Math.PI * 2)
                    ctx.stroke()
                }
            }

            // Longitude lines (rotating verticals)
            const numLongLines = 12
            for (let i = 0; i < numLongLines; i++) {
                const angle = (i / numLongLines) * Math.PI * 2 + rotation

                ctx.save()
                ctx.rotate(angle)

                // Only draw front-facing lines
                const isFront = Math.cos(angle) > 0
                ctx.globalAlpha = isFront ? 0.2 : 0.08

                ctx.beginPath()
                ctx.ellipse(0, 0, radius * 0.25, radius * 0.95, 0, 0, Math.PI * 2)
                ctx.stroke()

                ctx.restore()
            }

            ctx.globalAlpha = 1

            // Continents (simplified land masses)
            ctx.fillStyle = 'rgba(16, 185, 129, 0.6)'
            ctx.strokeStyle = 'rgba(5, 150, 105, 0.4)'
            ctx.lineWidth = 0.5

            // Multiple landmasses rotating
            const landmasses = [
                // Landmass 1 (Americas-like)
                { x: 0.5, y: 0.1, width: 0.35, height: 0.5, angle: rotation * 1.2 + Math.PI * 0.2 },
                // Landmass 2 (Africa-like)
                { x: 0.2, y: -0.15, width: 0.3, height: 0.4, angle: rotation * 1.2 + Math.PI * 0.8 },
                // Landmass 3 (Asia-like)
                { x: -0.4, y: 0.2, width: 0.4, height: 0.35, angle: rotation * 1.2 + Math.PI * 1.3 },
                // Landmass 4 (Europe-like)
                { x: -0.3, y: -0.2, width: 0.25, height: 0.25, angle: rotation * 1.2 + Math.PI * 1.5 },
            ]

            landmasses.forEach(land => {
                ctx.save()
                ctx.rotate(land.angle)

                const distFromCenter = Math.sqrt(land.x * land.x + land.y * land.y)
                const visibility = Math.cos(land.angle + Math.atan2(land.y, land.x))

                if (visibility > -0.3) { // Only draw visible portions
                    ctx.globalAlpha = Math.max(0, visibility) * 0.8

                    ctx.beginPath()
                    ctx.ellipse(
                        land.x * radius,
                        land.y * radius,
                        land.width * radius,
                        land.height * radius,
                        land.angle * 0.3,
                        0,
                        Math.PI * 2
                    )
                    ctx.fill()
                    ctx.stroke()
                }

                ctx.restore()
            })

            ctx.globalAlpha = 1

            // Top highlight/shine
            const shineGradient = ctx.createRadialGradient(
                -radius * 0.35,
                -radius * 0.4,
                0,
                0,
                0,
                radius
            )
            shineGradient.addColorStop(0, 'rgba(255, 255, 255, 0.5)')
            shineGradient.addColorStop(0.2, 'rgba(255, 255, 255, 0.25)')
            shineGradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.05)')
            shineGradient.addColorStop(1, 'rgba(255, 255, 255, 0)')

            ctx.fillStyle = shineGradient
            ctx.beginPath()
            ctx.arc(0, 0, radius, 0, Math.PI * 2)
            ctx.fill()

            // Bottom shadow
            const shadowGradient = ctx.createRadialGradient(
                radius * 0.2,
                radius * 0.3,
                0,
                0,
                0,
                radius
            )
            shadowGradient.addColorStop(0, 'rgba(0, 0, 0, 0)')
            shadowGradient.addColorStop(0.6, 'rgba(0, 0, 0, 0.1)')
            shadowGradient.addColorStop(1, 'rgba(0, 0, 0, 0.3)')

            ctx.fillStyle = shadowGradient
            ctx.beginPath()
            ctx.arc(0, 0, radius, 0, Math.PI * 2)
            ctx.fill()

            ctx.restore()

            // Smooth rotation - slower speed
            rotation += 0.008
        }

        const interval = setInterval(drawGlobe, 1000 / 60)

        return () => clearInterval(interval)
    }, [])

    return (
        <div className="animated-globe">
            <canvas ref={canvasRef} className="globe-canvas" />
        </div>
    )
}
